// VOY-417 §3 — Travel style & preferences — INFERRED, CORRECTABLE.
//
// Renders the 8 fixed style attributes from `user_agent_profile.style_attributes`
// (VOY-503 schema): airline_class, hotel_tier, trip_length, seat, climate,
// regions, booking_window, checked_bag.
//
// Each attribute always renders. `has_data:false` shows a muted
// "Not enough data yet" — never a fabricated value.
//
// **Each attribute has a working "Correct" affordance** wired to the
// VOY-458 backend via useFactCorrection. A correction writes through
// /memory/correct to user_preference_overrides, which VOY-438's resolver
// makes outrank the inference. This is what makes "INFERRED · EDITABLE"
// honest.
//
// STALE-SPEC DEVIATION (flagged in PR): VOY-503's 8 style_attribute keys
// don't all have a 1:1 entry in VOY-438's PROFILE_DIMENSIONS list. We
// map the ones that have a natural counterpart (see
// STYLE_ATTRIBUTE_TO_DIMENSION in useFactCorrection.ts). For attributes
// without a backend dimension, Correct is rendered but disabled with a
// "Backend coverage pending" tooltip — a wire-point for the backend
// follow-up to extend PROFILE_DIMENSIONS.

import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import type { AgentProfile, AgentStyleAttributes, StyleAttributeKey, StyleAttributeValue } from '@/types/profile'
import { STYLE_ATTRIBUTE_TO_DIMENSION, type FactCorrectionApi } from './useFactCorrection'
import { CorrectionModal, type CorrectionModalOption } from './CorrectionModal'

export interface TravelStyleSectionProps {
  agentProfile: AgentProfile | null
  loading: boolean
  correction: FactCorrectionApi
}

// Ordered list — design's reading order, not the type's alphabetical default.
const ATTRIBUTE_ORDER: StyleAttributeKey[] = [
  'airline_class',
  'hotel_tier',
  'trip_length',
  'seat',
  'climate',
  'regions',
  'booking_window',
  'checked_bag',
]

const ATTRIBUTE_LABEL: Record<StyleAttributeKey, string> = {
  airline_class: 'Airline class',
  hotel_tier: 'Hotel tier',
  trip_length: 'Trip length',
  seat: 'Seat preference',
  climate: 'Climate',
  regions: 'Regions',
  booking_window: 'Booking window',
  checked_bag: 'Checked bag',
}

/**
 * Per-attribute pickers for the Correction modal. Only mapped attributes
 * with categorical option lists show a `<select>`; the rest fall back to
 * free-text input. The keys/labels here mirror the categorical options
 * already used by VOY-485's Travel preferences card so the user has a
 * consistent vocabulary across both pages.
 */
const ATTRIBUTE_OPTIONS: Partial<Record<StyleAttributeKey, CorrectionModalOption[]>> = {
  hotel_tier: [
    { value: 'budget', label: 'Budget' },
    { value: 'mid-range', label: 'Mid-range' },
    { value: 'luxury', label: 'Luxury' },
    { value: 'unique', label: 'Unique / boutique' },
  ],
  trip_length: [
    { value: 'short', label: 'Short hops (1–3 nights)' },
    { value: 'standard', label: 'Standard (4–7 nights)' },
    { value: 'long', label: 'Long stays (8+ nights)' },
  ],
  booking_window: [
    { value: 'spontaneous', label: 'Spontaneous (< 2 weeks)' },
    { value: 'planned', label: 'Planned (2–8 weeks)' },
    { value: 'far_ahead', label: 'Far ahead (2+ months)' },
  ],
}

interface ActiveCorrection {
  key: StyleAttributeKey
  dimension: string
}

export function TravelStyleSection({ agentProfile, loading, correction }: TravelStyleSectionProps) {
  const [active, setActive] = useState<ActiveCorrection | null>(null)
  const attrs: AgentStyleAttributes | null = agentProfile?.style_attributes ?? null
  const hasAnyData = attrs
    ? ATTRIBUTE_ORDER.some((k) => attrs[k]?.has_data === true)
    : false

  function openCorrect(key: StyleAttributeKey) {
    const dimension = STYLE_ATTRIBUTE_TO_DIMENSION[key]
    if (!dimension) return  // Disabled wire-point — no backend dim
    setActive({ key, dimension })
  }

  function closeCorrect() {
    setActive(null)
    correction.resetError()
  }

  async function applyCorrect(newValue: string, reason: string) {
    if (!active || !attrs) return
    const priorValue = attrs[active.key]?.value ?? null
    const ok = await correction.correct(active.dimension, newValue, {
      reason: reason || undefined,
      priorValue: priorValue ?? undefined,
    })
    if (ok) closeCorrect()
  }

  async function applyPin() {
    if (!active || !attrs) return
    const value = attrs[active.key]?.value ?? null
    if (value == null) {
      // Pin requires a value when no prior override exists. If we don't
      // have the inferred value to pin, the modal shouldn't have opened —
      // bail to a no-op rather than a backend 400.
      closeCorrect()
      return
    }
    const ok = await correction.pin(active.dimension, value)
    if (ok) closeCorrect()
  }

  return (
    <section className="card" aria-labelledby="style-h">
      <div className="card-h">
        <h3 id="style-h">Travel style &amp; preferences</h3>
        <span className="meta">INFERRED &middot; EDITABLE</span>
      </div>

      <p className="you-style-blurb">
        What Voy thinks about how you travel. Tap <strong>Correct</strong> on anything that&apos;s wrong —
        your correction overrides the inference.
      </p>

      <div className="you-attributes">
        {ATTRIBUTE_ORDER.map((key) => {
          const attr = attrs?.[key] ?? null
          const mappedDim = STYLE_ATTRIBUTE_TO_DIMENSION[key] ?? null
          const isPending =
            correction.state.saving
            && correction.state.pendingDimension === mappedDim
          return (
            <AttributeRow
              key={key}
              label={ATTRIBUTE_LABEL[key]}
              attr={attr}
              loading={loading}
              hasBackendDim={mappedDim !== null}
              onCorrect={() => openCorrect(key)}
              pending={isPending}
            />
          )
        })}
      </div>

      {!loading && !hasAnyData && (
        <p className="you-style-foot">
          Empty for now — once you search, book, or chat with the agent, Voy starts filling these in.
        </p>
      )}

      {active && attrs && (
        <CorrectionModal
          open
          onClose={closeCorrect}
          dimension={active.dimension}
          dimensionLabel={ATTRIBUTE_LABEL[active.key]}
          currentValue={attrs[active.key]?.value}
          options={ATTRIBUTE_OPTIONS[active.key]}
          saving={correction.state.saving}
          error={correction.state.error}
          onCorrect={applyCorrect}
          onPin={applyPin}
        />
      )}
    </section>
  )
}

function AttributeRow({
  label,
  attr,
  loading,
  hasBackendDim,
  onCorrect,
  pending,
}: {
  label: string
  attr: StyleAttributeValue | null
  loading: boolean
  hasBackendDim: boolean
  onCorrect: () => void
  pending: boolean
}) {
  const hasData = attr?.has_data === true && attr?.value != null
  const skeleton = loading && !attr

  return (
    <div className="you-attribute">
      <div className="you-attribute-body">
        <span className="lbl">{label.toUpperCase()}</span>
        {skeleton ? (
          <span className="you-attribute-skeleton" aria-busy="true" />
        ) : hasData ? (
          <>
            <span className="you-attribute-value">{String(attr?.value)}</span>
            {attr?.sub_detail && (
              <span className="you-attribute-sub">{attr.sub_detail}</span>
            )}
          </>
        ) : (
          <span className="you-attribute-empty">Not enough data yet</span>
        )}
      </div>
      <div className="you-attribute-actions">
        {hasBackendDim ? (
          <button
            type="button"
            className="btn-ghost"
            style={{ padding: '4px 10px', fontSize: 11 }}
            onClick={onCorrect}
            disabled={pending || !hasData}
            title={!hasData ? 'Nothing to correct yet — Voy hasn\'t inferred this.' : 'Correct this attribute'}
          >
            {pending && <Loader2 className="kw-spin" aria-hidden="true" style={{ marginRight: 4 }} />}
            Correct
          </button>
        ) : (
          <button
            type="button"
            className="btn-ghost"
            style={{ padding: '4px 10px', fontSize: 11, opacity: 0.5 }}
            disabled
            title="Backend coverage pending — this dimension isn't in the override list yet."
          >
            Correct
          </button>
        )}
      </div>
    </div>
  )
}
